import React, { useEffect, useState, useCallback } from 'react';
import { supplierAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Clock, CheckCircle2, Truck, FileText, Eye, AlertTriangle, Download } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
    accepted: { label: 'Accepte', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
    preparing: { label: 'En preparation', color: 'bg-indigo-100 text-indigo-700', icon: Package },
    ready: { label: 'Pret', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    delivered: { label: 'Livre', color: 'bg-green-100 text-green-700', icon: Truck },
};

export default function SupplierOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showValidate, setShowValidate] = useState(false);
    const [modifiedItems, setModifiedItems] = useState([]);
    const [validationNotes, setValidationNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadOrders = useCallback(async () => {
        try {
            const res = await supplierAPI.getOrders();
            setOrders(res.data.orders || []);
        } catch (err) {
            toast.error('Erreur chargement commandes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadOrders(); }, [loadOrders]);

    const openValidateModal = (order) => {
        setSelectedOrder(order);
        setModifiedItems(
            (order.items || []).map(item => ({
                ...item,
                delivered_quantity: item.delivered_quantity ?? item.quantity,
            }))
        );
        setValidationNotes(order.notes || '');
        setShowValidate(true);
    };

    const updateItemQty = (index, newQty) => {
        setModifiedItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], delivered_quantity: Math.max(0, Number(newQty)) };
            return updated;
        });
    };

    const handleValidate = async () => {
        if (!selectedOrder) return;
        setSubmitting(true);
        try {
            await supplierAPI.validateOrder(selectedOrder.id, {
                items: modifiedItems.map(i => ({
                    product_id: i.product_id,
                    delivered_quantity: i.delivered_quantity,
                })),
                notes: validationNotes,
            });
            toast.success('Preparation validee avec succes !');
            setShowValidate(false);
            loadOrders();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur validation');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadBL = async (orderId) => {
        try {
            const res = await supplierAPI.generateDeliveryNote(orderId, {});
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `BL-${orderId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Bon de livraison telecharge');
            loadOrders();
        } catch (err) {
            toast.error('Erreur generation BL');
        }
    };

    const pendingCount = orders.filter(o => o.status === 'pending').length;
    const readyCount = orders.filter(o => o.status === 'ready').length;

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">Chargement...</div></div>;

    return (
        <div className="space-y-6" data-testid="supplier-orders">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
                <p className="text-sm text-gray-500 mt-1">Ordres de preparation a traiter</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Total', value: orders.length, icon: Package, bg: 'bg-blue-50', color: 'text-blue-600' },
                    { label: 'A traiter', value: pendingCount, icon: Clock, bg: 'bg-amber-50', color: 'text-amber-600' },
                    { label: 'Prets', value: readyCount, icon: CheckCircle2, bg: 'bg-emerald-50', color: 'text-emerald-600' },
                    { label: 'Livres', value: orders.filter(o => o.status === 'delivered').length, icon: Truck, bg: 'bg-green-50', color: 'text-green-600' },
                ].map(s => (
                    <Card key={s.label} className="rounded-xl">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                                <s.icon className={`w-5 h-5 ${s.color}`} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{s.value}</p>
                                <p className="text-xs text-gray-500">{s.label}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Orders List */}
            {orders.length === 0 ? (
                <Card className="rounded-xl">
                    <CardContent className="py-12 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Aucun ordre de preparation pour le moment</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {orders.map(order => {
                        const st = STATUS_MAP[order.status] || STATUS_MAP.pending;
                        const StIcon = st.icon;
                        return (
                            <Card key={order.id} className="rounded-xl hover:shadow-md transition-shadow" data-testid={`supplier-order-${order.id}`}>
                                <CardContent className="p-5">
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-bold text-base">{order.supplier_order_number}</h3>
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                                                    <StIcon className="w-3 h-3" />
                                                    {st.label}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-gray-600">
                                                <div><span className="text-gray-400">Client :</span> {order.client_name}</div>
                                                <div><span className="text-gray-400">Cmd :</span> {order.client_order_number}</div>
                                                <div><span className="text-gray-400">Articles :</span> {order.items?.length || 0}</div>
                                                <div><span className="text-gray-400">Total :</span> <span className="font-bold">{(order.delivered_total || order.total || 0).toFixed(2)} EUR</span></div>
                                            </div>
                                            {order.delivery_date && (
                                                <p className="text-xs text-gray-400 mt-1">Livraison : {new Date(order.delivery_date).toLocaleDateString('fr-FR')}</p>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {order.status === 'pending' && (
                                                <Button
                                                    onClick={() => openValidateModal(order)}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                                                    data-testid={`validate-order-${order.id}`}
                                                >
                                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                                    Valider
                                                </Button>
                                            )}
                                            {order.status === 'ready' && (
                                                <Button
                                                    onClick={() => handleDownloadBL(order.id)}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                                                    data-testid={`download-bl-${order.id}`}
                                                >
                                                    <Download className="w-4 h-4 mr-1" />
                                                    Bon de Livraison
                                                </Button>
                                            )}
                                            {order.bl_number && (
                                                <span className="text-xs text-green-600 font-medium">{order.bl_number}</span>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => { setSelectedOrder(order); }}
                                                className="rounded-lg"
                                                data-testid={`view-order-${order.id}`}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Expanded items preview */}
                                    {selectedOrder?.id === order.id && !showValidate && (
                                        <div className="mt-4 pt-4 border-t">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-gray-500 text-xs">
                                                        <th className="text-left pb-2">Produit</th>
                                                        <th className="text-center pb-2">Qte cmd</th>
                                                        <th className="text-center pb-2">Qte livree</th>
                                                        <th className="text-right pb-2">P.U.</th>
                                                        <th className="text-right pb-2">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {order.items?.map((item, i) => (
                                                        <tr key={i} className="border-t">
                                                            <td className="py-2 font-medium">{item.product_name}</td>
                                                            <td className="text-center">{item.quantity}</td>
                                                            <td className="text-center">
                                                                {item.delivered_quantity != null ? (
                                                                    <span className={item.quantity_modified ? 'text-amber-600 font-bold' : ''}>
                                                                        {item.delivered_quantity}
                                                                        {item.quantity_modified && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="text-right">{(item.unit_price || item.price || 0).toFixed(2)} EUR</td>
                                                            <td className="text-right font-bold">{(item.total || 0).toFixed(2)} EUR</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Validate Modal */}
            <Dialog open={showValidate} onOpenChange={setShowValidate}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            Valider la preparation
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                            <p><span className="text-gray-500">Commande :</span> <strong>{selectedOrder?.client_order_number}</strong></p>
                            <p><span className="text-gray-500">Client :</span> {selectedOrder?.client_name}</p>
                        </div>

                        <div>
                            <p className="text-sm font-medium mb-2">Quantites livrees :</p>
                            <p className="text-xs text-gray-400 mb-3">Modifiez les quantites si le stock est insuffisant</p>

                            <div className="space-y-3">
                                {modifiedItems.map((item, index) => (
                                    <div key={item.product_id || item.product_name} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{item.product_name}</p>
                                            <p className="text-xs text-gray-400">Commandee : {item.quantity} {item.unit || 'u.'}</p>
                                        </div>
                                        <div className="w-28">
                                            <Label className="text-xs text-gray-500">Livree</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                max={item.quantity * 2}
                                                value={item.delivered_quantity}
                                                onChange={(e) => updateItemQty(index, e.target.value)}
                                                className={`h-9 text-center ${item.delivered_quantity !== item.quantity ? 'border-amber-400 bg-amber-50' : ''}`}
                                                data-testid={`qty-input-${index}`}
                                            />
                                        </div>
                                        {item.delivered_quantity !== item.quantity && (
                                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-sm font-medium">Notes (optionnel)</Label>
                            <textarea
                                value={validationNotes}
                                onChange={(e) => setValidationNotes(e.target.value)}
                                className="w-full mt-1 p-2 border rounded-lg text-sm resize-none h-20"
                                placeholder="Commentaires sur la preparation..."
                                data-testid="validation-notes"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowValidate(false)}>Annuler</Button>
                        <Button
                            onClick={handleValidate}
                            disabled={submitting}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="confirm-validate"
                        >
                            {submitting ? 'Validation...' : 'Confirmer la preparation'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
